const STORAGE_KEY = 'dz-delivery-location-v1';
const LEGACY_SKIPPED_KEY = 'dz-delivery-location-skipped-v1';
const CHANGE_EVENT = 'dz-delivery-location:change';
const DEFAULT_CENTER = { lat: 24.7136, lng: 46.6753 };
const APPROXIMATE_ACCURACY_METRES = 150;
const PRECISE_ACCURACY_METRES = 50;
const MIN_CONFIRM_ZOOM = 17;
const KEEP_PLACEMENT_MIN_ZOOM = 15;
const SAME_PIN_DISTANCE_METRES = 30;
const DIGIT_PATTERN = /[0-9٠-٩۰-۹]/;
const NUMERIC_PATTERN = /^[0-9٠-٩۰-۹]+$/;
const ARABIC_LETTER_PATTERN = /[؀-ۿ]/;
const KEYBOARD_PAN_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

function readConfig() {
  const element = document.querySelector('script[data-dz-location-config]');
  if (!element) return null;
  try {
    return JSON.parse(element.textContent);
  } catch {
    return null;
  }
}

function loadState() {
  try {
    const value = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    return value && value.address ? value : null;
  } catch {
    return null;
  }
}

function saveState(state) {
  try {
    if (state) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {}
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

try {
  sessionStorage.removeItem(LEGACY_SKIPPED_KEY);
} catch {}

/** Names the cart surface an element sits in, so events can be split by drawer vs cart page. */
function surfaceOf(element) {
  return element?.closest?.('cart-drawer-component') ? 'cart_drawer' : 'cart_page';
}

function cameFromCheckout() {
  // Deliberately referrer-only. The ?dz=edit-items flag on the checkout link would also identify the
  // previous page, but it survives reloads and Horizon's in-place cart updates, so it would suppress
  // the location step for the rest of the visit; the referrer clears by itself on the next navigation.
  try {
    const referrer = new URL(document.referrer);
    return referrer.origin === window.location.origin && /\/checkouts?\//.test(referrer.pathname);
  } catch {
    return false;
  }
}

function normaliseRoot(root) {
  const value = root || '/';
  return value.endsWith('/') ? value : `${value}/`;
}

function normaliseArabic(value) {
  return value.replace(/[ً-ْٰ]/g, '').replace(/ى/g, 'ي');
}

function composeAddressLine1(address, language) {
  const separator = language === 'ar' ? '، ' : ', ';
  let district = (address.district || '').trim();
  if (district && language === 'ar' && ARABIC_LETTER_PATTERN.test(district) && !/^حي(\s|$)/.test(normaliseArabic(district))) {
    district = `حي ${district}`;
  }
  return [address.street, district].filter(Boolean).join(separator);
}

function formatAddress(address, language) {
  const separator = language === 'ar' ? '، ' : ', ';
  return [composeAddressLine1(address, language), address.unit, address.city, address.zip].filter(Boolean).join(separator);
}

function distanceMetres(a, b) {
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const earthRadius = 6371000;
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(a.lat)) * Math.cos(toRadians(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.sqrt(h));
}

let mapsPromise = null;
let authFailureHandler = null;

window.gm_authFailure = () => {
  if (authFailureHandler) authFailureHandler();
};

function loadGoogleMaps(apiKey, language) {
  if (window.google?.maps?.importLibrary) return Promise.resolve(window.google.maps);
  if (mapsPromise) return mapsPromise;

  mapsPromise = new Promise((resolve, reject) => {
    const callbackName = '__dzGoogleMapsReady';
    const timer = window.setTimeout(() => reject(new Error('timeout')), 15000);
    window[callbackName] = () => {
      window.clearTimeout(timer);
      resolve(window.google.maps);
    };
    const params = new URLSearchParams({
      key: apiKey,
      language: language === 'ar' ? 'ar' : 'en',
      region: 'SA',
      loading: 'async',
      callback: callbackName,
      v: 'weekly',
    });
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
    script.async = true;
    script.onerror = () => {
      window.clearTimeout(timer);
      reject(new Error('load'));
    };
    document.head.appendChild(script);
  });

  mapsPromise.catch(() => {
    mapsPromise = null;
  });

  return mapsPromise;
}

function findComponent(results, ...types) {
  for (const type of types) {
    for (const result of results) {
      const component = result.address_components.find((item) => item.types.includes(type));
      if (component) return component;
    }
  }
  return null;
}

function parseGeocodeResults(results, language) {
  const ordered = [...results].sort((a, b) => rank(a) - rank(b));
  const primary = ordered.slice(0, 1);
  const pick = (...types) => (findComponent(primary, ...types) || findComponent(ordered, ...types))?.long_name || '';
  const streetNumber = findComponent(primary, 'street_number')?.long_name || '';
  const premise = findComponent(primary, 'premise')?.long_name || '';
  const number = streetNumber || (NUMERIC_PATTERN.test(premise) ? premise : '');
  const route = pick('route');
  const street = language === 'ar' ? [route, number].filter(Boolean).join(' ') : [number, route].filter(Boolean).join(' ');
  const locationType = primary[0]?.geometry?.location_type || '';

  return {
    street,
    district: pick('sublocality_level_1', 'sublocality', 'neighborhood', 'administrative_area_level_3'),
    city: pick('locality', 'administrative_area_level_2', 'postal_town'),
    zip: pick('postal_code'),
    country: findComponent(ordered, 'country')?.short_name || '',
    buildingNumber: number,
    buildingNumberExact: Boolean(number) && locationType === 'ROOFTOP',
  };

  function rank(result) {
    if (result.types.includes('street_address') || result.types.includes('premise')) return 0;
    if (result.types.includes('route')) return 1;
    if (result.types.includes('plus_code')) return 3;
    return 2;
  }
}

function createElement(tag, attributes = {}, children = []) {
  const element = document.createElement(tag);
  for (const [key, value] of Object.entries(attributes)) {
    if (value === false || value == null) continue;
    if (key === 'text') {
      element.textContent = value;
    } else if (value === true) {
      element.setAttribute(key, '');
    } else {
      element.setAttribute(key, value);
    }
  }
  for (const child of children) element.append(child);
  return element;
}

function isVisible(element) {
  if (!element || !element.isConnected) return false;
  if (typeof element.checkVisibility === 'function') return element.checkVisibility();
  return element.getClientRects().length > 0;
}

class LocationDialog {
  constructor(config) {
    this.config = config;
    this.strings = config.strings;
    this.map = null;
    this.geocoder = null;
    this.mapReady = false;
    this.mapFailed = false;
    this.pendingPosition = null;
    this.pinPlaced = false;
    this.coordinates = null;
    this.locateToken = 0;
    this.keyboardPanPending = false;
    this.missingBuilding = false;
    this.buildingWarningShownOnSave = false;
    this.returnFocus = null;
    this.checkoutMode = false;
    this.savedAddress = null;
    this.skipTrigger = null;
    this.confirmToken = 0;
    this.build();
  }

  build() {
    const s = this.strings;
    this.dialog = createElement('dialog', {
      class: 'dz-location-dialog',
      'aria-labelledby': 'dz-location-dialog-title',
    });

    const closeButton = createElement('button', {
      type: 'button',
      class: 'dz-location-dialog__close button-unstyled',
      'aria-label': s.close,
      text: '×',
    });
    closeButton.addEventListener('click', () => this.close());

    const header = createElement('div', { class: 'dz-location-dialog__top' }, [
      (this.titleElement = createElement('h2', { id: 'dz-location-dialog-title', class: 'dz-location-dialog__title', text: s.dialogTitle })),
      closeButton,
    ]);

    this.searchInput = createElement('input', {
      type: 'search',
      name: 'dz-location-search',
      enterkeyhint: 'search',
      autocomplete: 'off',
      placeholder: s.searchPlaceholder,
      'aria-label': s.searchPlaceholder,
    });
    const searchForm = createElement('form', { class: 'dz-location-dialog__search', role: 'search' }, [
      this.searchInput,
      createElement('button', { type: 'submit', class: 'button', text: s.searchButton }),
    ]);
    searchForm.addEventListener('submit', (event) => {
      event.preventDefault();
      this.search();
    });

    this.mapElement = createElement('div', {
      class: 'dz-location-dialog__map',
      role: 'region',
      'aria-label': s.mapLabel,
    });
    this.mapElement.addEventListener('keydown', (event) => {
      if (KEYBOARD_PAN_KEYS.has(event.key)) this.keyboardPanPending = true;
    }, true);
    const pin = createElement('span', { class: 'dz-location-dialog__pin', 'aria-hidden': 'true' });
    pin.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 44" width="36" height="44"><path d="M18 43s15-13.4 15-25A15 15 0 0 0 3 18c0 11.6 15 25 15 25Z" fill="#d32f2f" stroke="#fff" stroke-width="2"/><circle cx="18" cy="18" r="5.5" fill="#fff"/></svg>';
    const locateButton = createElement('button', {
      type: 'button',
      class: 'dz-location-dialog__locate',
      text: s.locateMe,
    });
    locateButton.addEventListener('click', () => this.locate());

    this.status = createElement('p', { class: 'dz-location-dialog__status', role: 'status', 'aria-live': 'polite' });

    this.confirmButton = createElement('button', { type: 'button', class: 'button', text: s.confirmPin, disabled: true });
    this.confirmButton.addEventListener('click', () => this.confirmPin());
    this.manualButton = createElement('button', {
      type: 'button',
      class: 'dz-location-dialog__text-button',
      text: s.enterManually,
    });
    this.manualButton.addEventListener('click', () => {
      this.cancelPendingLocate();
      this.coordinates = null;
      this.savedAddress = null;
      this.showFields(this.currentAddress());
    });

    this.skipButtons = [s.enterManually, s.enterInCheckout].map((label) => {
      const button = createElement('button', {
        type: 'button',
        class: 'dz-location-dialog__text-button dz-location-dialog__skip',
        text: label,
        hidden: true,
      });
      button.addEventListener('click', () => {
        window.dzTrack?.('dz_location_manual_selected', {
          dz_surface: 'location_dialog',
          dz_stage: this.mapFailed ? 'map_unavailable' : 'map',
        });
        this.showSkipConfirm(button);
      });
      return button;
    });

    this.skipConfirmContinue = createElement('button', { type: 'button', class: 'button', text: s.skipConfirmContinue });
    this.skipConfirmContinue.addEventListener('click', () => this.skipToCheckout());
    const skipConfirmCancel = createElement('button', {
      type: 'button',
      class: 'dz-location-dialog__text-button',
      text: s.skipConfirmCancel,
    });
    skipConfirmCancel.addEventListener('click', () => this.hideSkipConfirm(true));
    this.skipConfirm = createElement(
      'div',
      { class: 'dz-location-dialog__confirm', role: 'group', 'aria-labelledby': 'dz-location-dialog-confirm-text', hidden: true },
      [
        createElement('p', { id: 'dz-location-dialog-confirm-text', class: 'dz-location-dialog__status', text: s.skipConfirmMessage }),
        this.skipConfirmContinue,
        skipConfirmCancel,
      ]
    );

    this.mapView = createElement('div', { class: 'dz-location-dialog__view' }, [
      searchForm,
      createElement('div', { class: 'dz-location-dialog__map-wrap' }, [this.mapElement, pin, locateButton]),
      this.status,
      createElement('div', { class: 'dz-location-dialog__footer' }, [this.confirmButton, this.manualButton, this.skipButtons[0]]),
    ]);

    this.fieldsStatus = createElement('p', {
      id: 'dz-location-dialog-fields-status',
      class: 'dz-location-dialog__status',
      role: 'status',
      'aria-live': 'polite',
    });
    this.inputs = {
      street: this.field('street', s.street, { required: true, autocomplete: 'address-line1' }),
      district: this.field('district', s.district, { autocomplete: 'address-level3' }),
      unit: this.field('unit', s.unit, { optional: true, autocomplete: 'address-line2' }),
      city: this.field('city', s.city, { required: true, autocomplete: 'address-level2' }),
      zip: this.field('zip', `${s.zip}`, { optional: true, autocomplete: 'postal-code', inputmode: 'numeric', maxlength: '10' }),
    };
    this.inputs.street.input.addEventListener('input', () => {
      if (DIGIT_PATTERN.test(this.inputs.street.input.value)) this.clearBuildingWarning();
    });
    this.backButton = createElement('button', { type: 'button', class: 'dz-location-dialog__text-button', text: s.backToMap });
    this.backButton.addEventListener('click', () => this.showMap());

    this.fieldsForm = createElement('form', { class: 'dz-location-dialog__view', hidden: true, novalidate: true }, [
      createElement('p', { class: 'dz-location-dialog__status', text: s.reviewHint }),
      this.fieldsStatus,
      ...Object.values(this.inputs).map((item) => item.label),
      createElement('div', { class: 'dz-location-dialog__footer' }, [
        (this.saveButton = createElement('button', { type: 'submit', class: 'button', text: s.saveAddress })),
        this.backButton,
        this.skipButtons[1],
      ]),
    ]);
    this.fieldsForm.addEventListener('submit', (event) => {
      event.preventDefault();
      this.save();
    });

    this.dialog.append(
      createElement('div', { class: 'dz-location-dialog__inner' }, [header, this.mapView, this.fieldsForm, this.skipConfirm])
    );
    this.dialog.addEventListener('close', () => {
      authFailureHandler = null;
      this.confirmToken += 1;
      this.cancelPendingLocate();
      this.hideSkipConfirm();
      this.restoreFocus();
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    document.body.append(this.dialog);
  }

  field(name, labelText, { required = false, optional = false, ...attributes } = {}) {
    const input = createElement('input', { type: 'text', name, required, ...attributes });
    const caption = createElement('span', { text: labelText });
    if (optional) caption.append(' ', createElement('small', { text: this.strings.optional }));
    const label = createElement('label', { class: 'dz-location-dialog__field' }, [caption, input]);
    return { input, label };
  }

  reset() {
    this.cancelPendingLocate();
    for (const item of Object.values(this.inputs)) item.input.value = '';
    this.coordinates = null;
    this.savedAddress = null;
    this.pendingPosition = null;
    this.pinPlaced = false;
    this.missingBuilding = false;
    this.buildingWarningShownOnSave = false;
    this.clearBuildingWarning();
    this.hideSkipConfirm();
  }

  setCheckoutMode(enabled) {
    const s = this.strings;
    this.checkoutMode = enabled;
    this.titleElement.textContent = enabled ? s.promptTitle : s.dialogTitle;
    this.saveButton.textContent = enabled ? s.continueToCheckout : s.saveAddress;
    this.manualButton.hidden = enabled;
    this.hideSkipConfirm();
    this.updateSkipButtons();
  }

  updateSkipButtons() {
    this.skipButtons[0].hidden = !this.checkoutMode;
    this.skipButtons[1].hidden = !(this.checkoutMode && this.mapFailed);
  }

  showSkipConfirm(trigger) {
    this.confirmToken += 1;
    this.cancelPendingLocate();
    this.skipTrigger = trigger;
    this.dialog.setAttribute('data-confirming', '');
    this.skipConfirm.hidden = false;
    this.skipConfirmContinue.focus();
  }

  hideSkipConfirm(returnFocus = false) {
    if (this.skipConfirm.hidden) return;
    this.skipConfirm.hidden = true;
    this.dialog.removeAttribute('data-confirming');
    if (returnFocus && isVisible(this.skipTrigger)) this.skipTrigger.focus();
  }

  open(mode, trigger) {
    this.returnFocus = trigger || document.activeElement;
    authFailureHandler = () => this.handleMapFailure();
    this.setCheckoutMode(mode === 'checkout');
    if (!this.dialog.open) this.dialog.showModal();

    const state = loadState();
    if (mode === 'checkout') {
      this.reset();
      this.showMap();
      if (this.mapReady) this.setStatus(this.status, this.strings.promptHint);
      return;
    }
    if (mode === 'locate' || (mode === 'change' && state && state.lat == null)) {
      this.reset();
      this.showMap();
      this.locate();
      return;
    }
    if (mode === 'change' && state) {
      this.reset();
      this.coordinates = { lat: state.lat, lng: state.lng };
      this.savedAddress = { street: '', district: '', unit: '', city: '', zip: '', ...state.address };
      for (const key of Object.keys(this.inputs)) this.inputs[key].input.value = this.savedAddress[key] || '';
      this.showMap();
      return;
    }

    if (state) {
      this.cancelPendingLocate();
      this.coordinates = state.lat != null ? { lat: state.lat, lng: state.lng } : null;
      this.missingBuilding = !DIGIT_PATTERN.test(state.address.street || '');
      this.buildingWarningShownOnSave = false;
      this.showFields({ street: '', district: '', unit: '', city: '', zip: '', ...state.address });
    } else {
      this.reset();
      this.showFields(null);
    }
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  restoreFocus() {
    if (isVisible(this.returnFocus)) {
      this.returnFocus.focus();
      return;
    }
    const edit = [...document.querySelectorAll('dz-delivery-location [data-dz-action="edit"]')].find(isVisible);
    const checkout = [...document.querySelectorAll('#checkout')].find(isVisible);
    (edit || checkout)?.focus();
  }

  setStatus(element, message, tone) {
    element.textContent = message || '';
    if (tone) {
      element.dataset.tone = tone;
    } else {
      delete element.dataset.tone;
    }
  }

  cancelPendingLocate() {
    this.locateToken += 1;
    if (this.locating) {
      this.locating = false;
      if (this.mapReady) this.setStatus(this.status, this.strings.statusMovePin);
    }
  }

  markPlacedByUser() {
    this.cancelPendingLocate();
    this.hideSkipConfirm();
    this.savedAddress = null;
    this.pinPlaced = true;
  }

  moveMap(center, zoom) {
    if (!this.map) return;
    if (center) this.map.setCenter(center);
    if (zoom != null) this.map.setZoom(zoom);
  }

  showMap() {
    this.hideSkipConfirm();
    if (this.mapFailed) {
      this.showFields(this.currentAddress(), this.strings.statusMapFailed);
      return;
    }
    this.fieldsForm.hidden = true;
    this.mapView.hidden = false;
    if (this.mapReady) {
      if (this.coordinates) {
        this.moveMap(this.coordinates, MIN_CONFIRM_ZOOM);
        this.pinPlaced = true;
      }
      if (!this.locating) this.setStatus(this.status, this.strings.statusMovePin);
    }
    this.ensureMap();
  }

  showFields(address, message) {
    this.hideSkipConfirm();
    this.mapView.hidden = true;
    this.fieldsForm.hidden = false;
    this.backButton.hidden = this.mapFailed;
    this.updateSkipButtons();
    if (address) {
      for (const key of Object.keys(this.inputs)) {
        if (key in address) this.inputs[key].input.value = address[key] || '';
      }
    }
    let text = message || '';
    const streetHasNumber = DIGIT_PATTERN.test(this.inputs.street.input.value);
    if (!text && this.missingBuilding && !streetHasNumber) text = this.strings.statusAddBuilding;
    this.setStatus(this.fieldsStatus, '', null);
    const street = this.inputs.street.input;
    if (text === this.strings.statusAddBuilding) {
      street.setAttribute('aria-describedby', this.fieldsStatus.id);
      street.setAttribute('aria-invalid', 'true');
    } else if (text) {
      street.setAttribute('aria-describedby', this.fieldsStatus.id);
      street.removeAttribute('aria-invalid');
    } else {
      this.clearBuildingWarning();
    }
    window.requestAnimationFrame(() => this.setStatus(this.fieldsStatus, text, text ? 'warning' : null));
    const firstEmpty = Object.values(this.inputs).find((item) => item.input.required && !item.input.value);
    const target = firstEmpty || street;
    target.focus();
  }

  clearBuildingWarning() {
    const street = this.inputs.street.input;
    street.removeAttribute('aria-invalid');
    street.removeAttribute('aria-describedby');
    if (this.fieldsStatus.textContent === this.strings.statusAddBuilding) this.setStatus(this.fieldsStatus, '', null);
  }

  currentAddress() {
    const address = {};
    for (const key of Object.keys(this.inputs)) address[key] = this.inputs[key].input.value.trim();
    return Object.values(address).some(Boolean) ? address : loadState()?.address || null;
  }

  async ensureMap() {
    if (this.mapReady || this.mapLoading) return;
    this.mapLoading = true;
    this.setStatus(this.status, this.strings.statusLoading);
    try {
      const maps = await loadGoogleMaps(this.config.apiKey, this.config.language);
      const { Map } = await maps.importLibrary('maps');
      const { Geocoder } = await maps.importLibrary('geocoding');
      const start = this.pendingPosition || this.coordinates;
      if (!this.pendingPosition && this.coordinates) this.pinPlaced = true;
      this.map = new Map(this.mapElement, {
        center: start || DEFAULT_CENTER,
        zoom: start ? MIN_CONFIRM_ZOOM : 11,
        disableDefaultUI: true,
        zoomControl: true,
        clickableIcons: false,
        gestureHandling: 'greedy',
      });
      this.map.addListener('click', (event) => {
        this.map.panTo(event.latLng);
        this.markPlacedByUser();
      });
      this.map.addListener('dragstart', () => {
        this.cancelPendingLocate();
        const zoomedOut = this.map.getZoom() < MIN_CONFIRM_ZOOM;
        this.setStatus(this.status, zoomedOut ? this.strings.statusZoomIn : this.strings.statusMovePin);
      });
      this.map.addListener('dragend', () => this.markPlacedByUser());
      this.map.addListener('idle', () => {
        if (!this.keyboardPanPending) return;
        this.keyboardPanPending = false;
        this.markPlacedByUser();
      });
      this.geocoder = new Geocoder();
      this.mapReady = true;
      this.confirmButton.disabled = false;
      if (!this.status.dataset.tone && !this.locating) {
        this.setStatus(this.status, this.checkoutMode && !this.pinPlaced ? this.strings.promptHint : this.strings.statusMovePin);
      }
    } catch {
      this.handleMapFailure();
    } finally {
      this.mapLoading = false;
    }
  }

  handleMapFailure() {
    this.mapFailed = true;
    this.mapReady = false;
    this.confirmButton.disabled = true;
    if (this.dialog.open) this.showFields(this.currentAddress(), this.strings.statusMapFailed);
  }

  locate() {
    if (!('geolocation' in navigator)) {
      this.setStatus(this.status, this.strings.statusUnavailable, 'warning');
      return;
    }
    this.hideSkipConfirm();
    this.locateToken += 1;
    const token = this.locateToken;
    this.locating = true;
    this.setStatus(this.status, this.strings.statusLocating);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (token !== this.locateToken) return;
        this.locating = false;
        this.savedAddress = null;
        const { latitude, longitude, accuracy } = position.coords;
        const point = { lat: latitude, lng: longitude };
        this.pendingPosition = point;
        this.pinPlaced = accuracy <= PRECISE_ACCURACY_METRES;
        const approximate = accuracy > APPROXIMATE_ACCURACY_METRES;
        this.moveMap(point, approximate ? KEEP_PLACEMENT_MIN_ZOOM : MIN_CONFIRM_ZOOM);
        if (approximate) {
          this.setStatus(this.status, this.strings.statusApproximate, 'warning');
        } else {
          this.setStatus(this.status, this.strings.statusMovePin);
        }
      },
      (error) => {
        if (token !== this.locateToken) return;
        this.locating = false;
        const denied = error.code === error.PERMISSION_DENIED;
        this.setStatus(this.status, denied ? this.strings.statusDenied : this.strings.statusUnavailable, 'warning');
        this.searchInput.focus();
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  }

  async search() {
    const query = this.searchInput.value.trim();
    if (!query || !this.geocoder) return;
    this.cancelPendingLocate();
    this.hideSkipConfirm();
    this.savedAddress = null;
    this.setStatus(this.status, this.strings.statusGeocoding);
    try {
      const { results } = await this.geocoder.geocode({
        address: query,
        componentRestrictions: { country: 'SA' },
        language: this.config.language === 'ar' ? 'ar' : 'en',
      });
      const result = results?.[0];
      if (!result) throw new Error('empty');
      const isAddress = result.types.includes('street_address') || result.types.includes('premise');
      const isExact = isAddress && !result.partial_match && result.geometry.location_type === 'ROOFTOP';
      if (result.geometry.viewport && !isAddress) {
        this.map.fitBounds(result.geometry.viewport);
        this.pinPlaced = false;
        const idleListener = this.map.addListener('idle', () => {
          idleListener.remove();
          const zoomedOut = this.map.getZoom() < MIN_CONFIRM_ZOOM;
          this.setStatus(this.status, zoomedOut ? this.strings.statusZoomIn : this.strings.statusPlacePin);
        });
      } else {
        this.moveMap(result.geometry.location, MIN_CONFIRM_ZOOM);
        this.pinPlaced = isExact;
        this.setStatus(this.status, isExact ? this.strings.statusMovePin : this.strings.statusPlacePin);
      }
    } catch {
      this.setStatus(this.status, this.strings.statusSearchNoResult, 'warning');
    }
  }

  async confirmPin() {
    if (!this.map || !this.geocoder) return;
    const zoom = this.map.getZoom();
    if (zoom < MIN_CONFIRM_ZOOM) {
      const keepPlacement = this.pinPlaced && zoom >= KEEP_PLACEMENT_MIN_ZOOM;
      this.map.setZoom(MIN_CONFIRM_ZOOM);
      this.pinPlaced = keepPlacement;
      this.setStatus(this.status, keepPlacement ? this.strings.statusZoomedCheck : this.strings.statusZoomIn, 'warning');
      return;
    }
    if (!this.pinPlaced) {
      this.setStatus(this.status, this.strings.statusPlacePin, 'warning');
      return;
    }
    const center = this.map.getCenter();
    const point = { lat: center.lat(), lng: center.lng() };
    const samePin = this.coordinates && distanceMetres(this.coordinates, point) <= SAME_PIN_DISTANCE_METRES;
    if (samePin && this.savedAddress) {
      const saved = this.savedAddress;
      this.savedAddress = null;
      this.missingBuilding = !DIGIT_PATTERN.test(saved.street);
      this.buildingWarningShownOnSave = false;
      this.showFields(saved);
      return;
    }
    this.confirmToken += 1;
    const token = this.confirmToken;
    this.confirmButton.disabled = true;
    this.setStatus(this.status, this.strings.statusGeocoding);
    try {
      const { results } = await this.geocoder.geocode({
        location: point,
        language: this.config.language === 'ar' ? 'ar' : 'en',
      });
      if (token !== this.confirmToken) {
        this.setStatus(this.status, this.strings.statusMovePin);
        return;
      }
      if (!results?.length) throw new Error('empty');
      const address = parseGeocodeResults(results, this.config.language);
      if (address.country && address.country !== 'SA') {
        this.setStatus(this.status, this.strings.statusOutsideSa, 'warning');
        return;
      }
      this.coordinates = point;
      this.savedAddress = null;
      const { buildingNumber, buildingNumberExact, country, ...fields } = address;
      if (!samePin) fields.unit = '';
      this.missingBuilding = !buildingNumber;
      this.buildingWarningShownOnSave = false;
      this.setStatus(this.status, this.strings.statusMovePin);
      let message = '';
      if (!buildingNumber) message = this.strings.statusAddBuilding;
      else if (!buildingNumberExact) message = this.strings.statusCheckBuilding;
      this.showFields(fields, message);
    } catch (error) {
      if (token !== this.confirmToken) {
        this.setStatus(this.status, this.strings.statusMovePin);
        return;
      }
      if (error?.code === 'REQUEST_DENIED') {
        this.handleMapFailure();
        return;
      }
      this.coordinates = point;
      this.savedAddress = null;
      this.missingBuilding = true;
      this.buildingWarningShownOnSave = false;
      this.setStatus(this.status, this.strings.statusMovePin);
      this.showFields({ street: '', district: '', city: '', zip: '', ...(samePin ? {} : { unit: '' }) }, this.strings.statusNoResult);
    } finally {
      this.confirmButton.disabled = !this.mapReady;
    }
  }

  save() {
    if (!this.fieldsForm.reportValidity()) return;
    const address = {};
    for (const key of Object.keys(this.inputs)) address[key] = this.inputs[key].input.value.trim();
    if (!DIGIT_PATTERN.test(address.street) && !this.buildingWarningShownOnSave) {
      this.buildingWarningShownOnSave = true;
      this.missingBuilding = true;
      this.showFields(null, this.strings.statusAddBuilding);
      return;
    }
    saveState({
      address,
      lat: this.coordinates?.lat ?? null,
      lng: this.coordinates?.lng ?? null,
      source: this.coordinates ? 'map' : 'manual',
      savedAt: new Date().toISOString(),
    });
    const continueToCheckout = this.checkoutMode;
    window.dzTrack?.('dz_location_confirmed', {
      dz_method: this.coordinates ? 'map' : 'manual',
      dz_opened_from: continueToCheckout ? 'checkout' : 'cart',
      dz_continued_to_checkout: continueToCheckout,
    });
    this.close();
    if (continueToCheckout) proceedToCheckout(this.config);
  }

  skipToCheckout() {
    window.dzTrack?.('dz_location_skipped', {
      dz_opened_from: this.checkoutMode ? 'checkout' : 'cart',
      dz_stage: this.mapFailed ? 'map_unavailable' : 'map',
    });
    this.close();
    proceedToCheckout(this.config);
  }
}

let dialogInstance = null;

function getDialog(config) {
  if (!dialogInstance) dialogInstance = new LocationDialog(config);
  return dialogInstance;
}

class DzDeliveryLocation extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    document.addEventListener(CHANGE_EVENT, this.render);
    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    document.removeEventListener(CHANGE_EVENT, this.render);
  }

  handleClick = (event) => {
    const trigger = event.target.closest('[data-dz-action]');
    if (!trigger) return;
    const action = trigger.dataset.dzAction;
    if (action === 'clear') {
      window.dzTrack?.('dz_location_removed', { dz_surface: surfaceOf(this) });
      saveState(null);
      dialogInstance?.reset();
      // The cart has no location call to action any more, so send focus on to Check out,
      // which is where the shopper is asked for a delivery location.
      document.querySelector('#checkout')?.focus();
      return;
    }
    const config = readConfig();
    if (!config) return;
    if (action === 'edit') window.dzTrack?.('dz_location_changed', { dz_surface: surfaceOf(this) });
    const modes = { locate: 'locate', edit: 'change' };
    getDialog(config).open(modes[action] || 'fields', trigger);
  };

  render = () => {
    const state = loadState();
    this.toggleAttribute('data-has-address', Boolean(state));
    const summary = this.querySelector('[data-dz-summary]');
    if (summary) {
      const language = readConfig()?.language || document.documentElement.lang || 'en';
      summary.textContent = state ? formatAddress(state.address, language.startsWith('ar') ? 'ar' : 'en') : '';
      summary.title = summary.textContent;
    }
  };
}

if (!customElements.get('dz-delivery-location')) {
  customElements.define('dz-delivery-location', DzDeliveryLocation);
}

function buildCheckoutUrl(config, address) {
  const root = normaliseRoot(config.root);
  if (!config.prefill) return `${root}checkout`;
  const params = new URLSearchParams();
  params.set('checkout[shipping_address][address1]', composeAddressLine1(address, config.language));
  if (address.unit) params.set('checkout[shipping_address][address2]', address.unit);
  params.set('checkout[shipping_address][city]', address.city);
  if (address.zip) params.set('checkout[shipping_address][zip]', address.zip);
  params.set('checkout[shipping_address][country]', 'SA');
  return `${root}checkout?${params}`;
}

async function proceedToCheckout(config) {
  const state = loadState();
  const root = normaliseRoot(config.root);
  if (state) {
    try {
      await saveCoordinatesToCart(config, state);
    } catch {}
    window.location.assign(buildCheckoutUrl(config, state.address));
    return;
  }
  window.location.assign(`${root}checkout`);
}

async function saveCoordinatesToCart(config, state) {
  if (!config.saveCoordinates || state.lat == null) return;
  const root = normaliseRoot(config.root);
  const coordinates = `${state.lat.toFixed(6)},${state.lng.toFixed(6)}`;
  await fetch(`${root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      attributes: {
        'Delivery pin': coordinates,
        'Delivery map link': `https://www.google.com/maps?q=${coordinates}`,
      },
    }),
  });
}

if (!window.__dzDeliveryLocationCheckoutBound) {
  window.__dzDeliveryLocationCheckoutBound = true;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
      if (event.submitter?.getAttribute('name') !== 'checkout') return;
      const config = readConfig();
      if (!config) return;
      const state = loadState();
      if (!state) {
        // Shoppers who came back from checkout to change their items are not asked for a location
        // again on the way out; they were already asked before they reached checkout.
        if (!config.promptBeforeCheckout || cameFromCheckout()) return;
        event.preventDefault();
        window.dzTrack?.('dz_location_prompt_opened', {
          dz_trigger: 'checkout',
          dz_surface: surfaceOf(event.submitter),
        });
        getDialog(config).open('checkout', event.submitter);
        return;
      }
      if (!config.prefill && !config.saveCoordinates) return;
      event.preventDefault();
      event.submitter.setAttribute('aria-busy', 'true');
      event.submitter.disabled = true;
      proceedToCheckout(config);
    },
    true
  );

  window.addEventListener('pageshow', () => {
    document.querySelectorAll('#checkout[aria-busy="true"]').forEach((button) => {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    });
  });
}
